import { useEffect } from "react";
import { matchRoutes, useLocation } from "react-router-dom";

import { NavProfile } from "constants/field-values";
import { PAGE_TITLE_BY_ROUTE } from "constants/page-titles";
import { useNavProfile } from "hooks/useNavProfile";

const OLLIE_APP_NAME = "Ollie";

// index.html sets window.appBrand from the domain before React loads. Same names as the title that index.html sets.
const appBrand = (window as Window & { appBrand?: string }).appBrand;
const BRAND_APP_NAME = appBrand === "maestro" ? "MaestroSync" : "Highnotes Platform";

const PAGE_TITLE_ROUTES = Object.keys(PAGE_TITLE_BY_ROUTE).map(path => ({ path }));

/**
 * Returns the page name for the current URL, or null when no route matches.
 * matchRoutes ranks the routes like the router does, so "/guides/add" matches GUIDE_ADD and not GUIDE_DETAILS.
 */
const findPageTitle = (pathname: string): string | null => {
	const matchedRoutes = matchRoutes(PAGE_TITLE_ROUTES, pathname);
	const matchedPath = matchedRoutes?.[0]?.route.path;

	if (!matchedPath) {
		return null;
	}

	return PAGE_TITLE_BY_ROUTE[matchedPath];
};

/**
 * Sets the browser tab title to "<app name> - <page name>" on every navigation.
 * The app name is "Ollie" when the Ollie nav profile is active, otherwise the brand of the domain.
 * Must run inside the router, because it reads the current location.
 */
export const usePageTitle = () => {
	const { pathname } = useLocation();
	const { navProfile } = useNavProfile();
	const appName = navProfile.id === NavProfile.Ollie ? OLLIE_APP_NAME : BRAND_APP_NAME;

	useEffect(() => {
		const pageTitle = findPageTitle(pathname);
		document.title = pageTitle ? `${appName} - ${pageTitle}` : appName;
	}, [appName, pathname]);
};
